import { Observable, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { catchError } from "rxjs/operators";
import { BaseApiService } from "../base/base-api.service";
import { ClientUser } from "../../../models/client-users/client-user.model";
import { ClientUserSerializer } from "../../../models/client-users/client-user-serializer.class";
import { Injectable } from "@angular/core";
import { CurrentUser } from "../../../models/client-users/current-user.model";
import { PasswordPolicy } from "../../../models/client-users/password-policy.model";
import { CustomErrorHandlerService } from "../custom-error-handler.service";

/**
 * Service to communicate with API Client Users
 */

@Injectable()
export class ClientUsersService extends BaseApiService<ClientUser> {
  constructor(httpClient: HttpClient) {
    super(
      httpClient,
      null,
      "clientuser",
      new ClientUserSerializer(),
      new CustomErrorHandlerService()
    );
  }

  /**
   * The caller's own record for the active tenant (GET v1/clientuser/me).
   *
   * The Auth0 profile cannot name the user: 4Comply writes first/last name to `user_metadata`,
   * which no Rule copies into the ID token, and the `name` claim is the email address. This is
   * the only source for the signed-in person's real name and their role key for THIS tenant.
   *
   * Bare [FourComplyAuthorize] on the API — any authenticated user may read their own record.
   */
  public me(): Observable<CurrentUser> {
    return this.httpClient
      .get<CurrentUser>(`${this.url}/clientuser/me`)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError((): HttpErrorResponse => this.errorHandler.tryParseError(error)))
      );
  }

  /**
   * The password rules this tenant's Auth0 connection enforces
   * (`GET v1/clientuser/password-policy`), so the dialog can state them and check them before
   * submitting instead of letting Auth0 refuse after the fact.
   */
  public passwordPolicy(): Observable<PasswordPolicy> {
    return this.httpClient
      .get<PasswordPolicy>(`${this.url}/clientuser/password-policy`)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError((): HttpErrorResponse => this.errorHandler.tryParseError(error)))
      );
  }

  /**
   * Sets a new password on the caller's OWN account (POST v1/clientuser/me/password).
   *
   * The account is chosen server-side from the JWT, so nothing here names a user. By product
   * decision the current password is not required: a live session is sufficient.
   *
   * Distinct from resetPassword() below, which mails a reset link and is admin-only.
   */
  public changeOwnPassword(newPassword: string): Observable<unknown> {
    return this.httpClient
      .post<unknown>(`${this.url}/clientuser/me/password`, { new_password: newPassword })
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError((): HttpErrorResponse => this.errorHandler.tryParseError(error)))
      );
  }

  resetPassword(email: any): Observable<any> {
    return this.httpClient.post(
      `${this.url}/clientuser/resetpassword`,
      {
        email_address : email
      },
    );
  }

  // EXTEND/OVERRIDE THE SERVICE HERE IF NECESSARY
}
